import React, { useEffect, useMemo, useCallback } from 'react';
import { Stack, DraggableDialog } from '@gogame/components';
import { useScrollToDown } from 'components/AIChatbot/hooks/useScrollToDown';
import { useCalcMinHeightResponse } from 'components/AIChatbot/hooks/useCalcMinHeightResponse';

import { SendMessageForm } from './SendMessageForm';
import { LoadingContainer, ScrollContainer } from './StyledComponents';
import { TYPE_BOT, TYPE_USER } from '../constants';
import { useChatContext } from '../context';

export const ExistingChat = React.memo(
  ({
    onSubmitMessage,
    messages,
    isRefetching,
    isFetched,
    isLoading,
    slots,
  }) => {
    const MessageSlot = slots?.message;

    const { chatId: contextChatId } = useChatContext();

    const { boxRef, lastUserMessageRef, minHeight } =
      useCalcMinHeightResponse();

    if (!MessageSlot) {
      throw new Error('Message slot is required');
    }

    const handleSubmitMessage = useCallback(
      async ({ query }, { resetForm }) => {
        resetForm();
        await onSubmitMessage({ query });
      },
      [onSubmitMessage]
    );

    const scrollToDown = useScrollToDown('instant');

    //scroll if first open
    useEffect(() => {
      if (isFetched) {
        scrollToDown('[data-cy="View"] > [data-cy="Box"]');
      }
    }, [isFetched, contextChatId, scrollToDown]);

    const lastUserIndex = useMemo(
      () => messages.findLastIndex((message) => message.type === TYPE_USER),
      [messages]
    );

    return (
      <LoadingContainer loading={isRefetching}>
        <ScrollContainer ref={boxRef}>
          <DraggableDialog.Content>
            {messages.map((message, index) => {
              const key = [
                message.type,
                message.id ? message.id : `index-${index}`,
                message.typingIterator
                  ? `iterator-${message.typingIterator}`
                  : null,
              ]
                .filter((el) => el !== null)
                .join(':');

              const isLastBotResponse =
                index === messages.length - 1 && message.type === TYPE_BOT;
              const isLastUserRequest = index === lastUserIndex;

              return (
                <Stack
                  key={key}
                  component={'article'}
                  sx={{
                    m: 1,
                    ...(isLastBotResponse && minHeight && { minHeight }),
                  }}
                  ref={isLastUserRequest ? lastUserMessageRef : undefined}
                >
                  <MessageSlot message={message} />
                </Stack>
              );
            })}
          </DraggableDialog.Content>
        </ScrollContainer>
        <DraggableDialog.Content sx={{ p: 1 }}>
          <SendMessageForm
            onSubmit={handleSubmitMessage}
            isLoading={isLoading}
          />
        </DraggableDialog.Content>
      </LoadingContainer>
    );
  }
);
